import React, { useState, useEffect, useMemo } from 'react'; // Added useMemo to imports
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore'; 
import { auth, db } from './services/firebaseConfig.js'; 
import { useCRM } from './hooks/useCRM.js';
import { STAGES } from './constants/crmConstants.js';

// Component Imports
import AuthScreen from './components/AuthScreen.js';
import StudentCard from './components/StudentCard.js';
import FormModal from './components/FormModal.js';
import SettingsScreen from './components/SettingsScreen.js';
import UserManagement from './components/UserManagement.js';
import ProgramInformation from './components/ProgramInformation.js';
import SubmitApplication from './components/SubmitApplication.js';
import EvaluateApplication from './components/EvaluateApplication.js';
import GeminiAssistant from './components/GeminiAssistant.js';
import StudentProfile from './components/StudentProfile.js';

const App = () => {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [activeView, setActiveView] = useState('dashboard');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [appSectionTab, setAppSectionTab] = useState('dashboard');
  
  const [boardFilters, setBoardFilters] = useState({
    university: 'All Universities',
    targetCountry: 'All Countries',
    progressionMonth: 'All Months',
    progressionYear: 'All Years',
    termMonth: 'All Months',
    termYear: 'All Years',
    agent: 'All Agents',
    twgProgram: 'All Programs'
  });

  // NEW: State for user profile popup
  const [showProfile, setShowProfile] = useState(false);

  // 1. CORE PLATFORM SETTINGS (Manual data from Global Setup)
  const { students, applicants, users, settings, applications, actions, loading: crmLoading } = useCRM(user);
  
  // FIX: This ensures that the profile view always uses the most up-to-date
  // data from the real-time 'students' array synced from Firestore.
  const liveSelectedStudent = useMemo(() => {
    if (!selectedStudent) return null;
    return students.find(s => s.id === selectedStudent.id) || selectedStudent;
  }, [selectedStudent, students]);

  const filteredBoardStudents = useMemo(() => {
    return students.filter(s => {
      const matchUni = boardFilters.university === 'All Universities' || s.destinationUniversity === boardFilters.university;
      const matchCountry = boardFilters.targetCountry === 'All Countries' || s.targetCountry === boardFilters.targetCountry; // Added logic
      const matchProgMonth = boardFilters.progressionMonth === 'All Months' || s.expectedProgressionMonth === boardFilters.progressionMonth;
      const matchProgYear = boardFilters.progressionYear === 'All Years' || String(s.expectedProgressionYear) === boardFilters.progressionYear;
      const matchTermMonth = boardFilters.termMonth === 'All Months' || s.commencingMonth === boardFilters.termMonth;
      const matchTermYear = boardFilters.termYear === 'All Years' || String(s.commencingYear) === boardFilters.termYear;
      const matchAgent = boardFilters.agent === 'All Agents' || s.sourceAgent === boardFilters.agent;
      const matchProgram = boardFilters.twgProgram === 'All Programs' || s.twgProgram === boardFilters.twgProgram;
      
      return matchUni && matchCountry && matchProgMonth && matchProgYear && matchTermMonth && matchTermYear && matchAgent && matchProgram;
    });
  }, [students, boardFilters]);

  // 2. BOT-ONLY DATA (Completely separate from Platform Settings)
  const [botKnowledge, setBotKnowledge] = useState("");

  // Listen specifically to the App Script path for the Bot's usage only
  useEffect(() => {
    if (!user) return;

    const artifactsPath = doc(db, 'artifacts', 'worldgrad-crm', 'public', 'data', 'settings', 'programMappings');
    
    const unsubscribeBot = onSnapshot(artifactsPath, (docSnap) => {
      if (docSnap.exists()) {
        setBotKnowledge(docSnap.data().data || "");
      }
    });

    return () => unsubscribeBot();
  }, [user]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, []);

  
  const handleSaveApplicant = async (data, id) => {
    await actions.saveApplicant(data, id);
  };

  const handleDeleteApplicant = async (id) => {
    await actions.deleteApplicant(id);
  };

  const handleSaveApplication = async (newApp) => {
    const currentUser = users.find(u => String(u.uid) === String(user?.uid));
    const appWithAgent = {
      ...newApp,
      agent: currentUser?.name || user?.displayName || 'System User',
      partnerName: currentUser?.agentAssociation || 'Direct Entry'
    };
    await actions.saveApplication(appWithAgent);
  };

  const currentDate = new Date().toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });

  if (authLoading || (user && crmLoading)) {
    return (
      <div className="h-screen w-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-[#0464AD] border-t-transparent rounded-full animate-spin"></div>
          <p className="text-xs font-black text-[#0464AD] uppercase tracking-widest">Portal Syncing...</p>
        </div>
      </div>
    );
  }

  if (!user) return <AuthScreen logoUrl={null} />;

  const currentUserData = users.find(u => String(u.uid) === String(user.uid));

  if (currentUserData && currentUserData.status !== 'Approved') {
    return (
      <div className="h-screen w-full flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-[2.5rem] p-10 shadow-2xl border border-slate-100 text-center animate-in fade-in zoom-in-95 duration-500">
          <div className="text-5xl mb-6">{currentUserData.status === 'Disabled' ? '🚫' : '⏳'}</div>
          <h2 className="text-2xl font-black text-slate-900 tracking-tighter uppercase leading-none">
           {currentUserData.status === 'Disabled' ? 'Access Revoked' : 'Access Pending'}
         </h2>
         <p className="text-sm text-slate-500 font-medium mt-6 leading-relaxed">
           {currentUserData.status === 'Disabled' 
             ? "Your account has been disabled by an administrator. Please contact support if you believe this is an error."
             : `Hi ${currentUserData.name}, your account is currently pending administrator approval.`}
         </p>
         <button onClick={() => signOut(auth)} className="...">← Back to Login</button>
        </div>
      </div>
    );
  }

  const role = currentUserData?.role || '';
  const isAdmin = role === 'Admin';
  const isAgent = role === 'TWG Agent';
  const isAgentMaster = role === 'TWG Agent Master';
  const isAppsAndVisa = role === 'TWG Apps and Visa';
  const isB2B = role === 'TWG B2B User';

  const NavCard = ({ title, icon, onClick, description }) => (
    <button 
      onClick={onClick} 
      className="bg-white/80 p-5 rounded-2xl border-2 border-[#0464AD]/10 shadow-sm hover:shadow-md hover:border-[#F15E3B] transition-all text-left group flex items-center gap-4 w-full backdrop-blur-md"
    >
      {/* Icon container with a subtle brand-blue background */}
      <div className="text-2xl w-12 h-12 flex items-center justify-center bg-[#0464AD]/5 rounded-xl group-hover:scale-110 transition-transform flex-shrink-0">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-[13px] font-black tracking-tight text-slate-900 uppercase leading-none mb-1 group-hover:text-[#0464AD] transition-colors">
          {title}
        </h3>
        <p className="text-[11px] text-slate-400 font-medium leading-tight line-clamp-2">
          {description}
        </p>
      </div>
      <div className="text-[#0464AD] opacity-0 group-hover:opacity-100 transition-opacity pr-2">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </button>
  );

  return (
    <div className="min-h-screen font-sans selection:bg-[#0464AD]/10 selection:text-[#0464AD]">
      {/* HEADER */}
      <nav className="sticky top-0 z-[100] bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <img 
              src="https://drive.google.com/thumbnail?id=1eUXVAzqWFYmdnmhqGrVni9B14mWoZJlH&sz=w1000" 
              alt="The WorldGrad" 
              className="h-10 w-auto object-contain cursor-pointer -ml-1" // Added -ml-1 to nudge left if the image has whitespace
              onClick={() => setActiveView('dashboard')}
            />
            <button 
              onClick={() => setActiveView('dashboard')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                activeView === 'dashboard' ? 'bg-slate-900 text-white shadow-lg' : 'text-slate-400 hover:text-slate-900 hover:bg-slate-100'
              }`}
            >
              Home
            </button>
          </div>

          {/* ... Keep Right Side (Profile/Logout) as is ... */}
          <div className="flex items-center gap-4">
            {isAdmin && (
              <button onClick={() => setActiveView('users')} className={`px-5 py-2.5 rounded-xl text-[10px] font-black uppercase transition-all ${activeView === 'users' ? 'bg-[#0464AD] text-white' : 'bg-slate-100 text-slate-500'}`}>Users</button>
            )}
            <button onClick={() => setShowProfile(true)} className="p-2 text-slate-400 hover:text-[#0464AD] transition-colors">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
            </button>
            <button onClick={() => signOut(auth)} className="p-2 text-slate-400 hover:text-red-500 transition-colors">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
            </button>
          </div>
        </div>
      </nav>

      <main className="py-12 px-8 max-w-7xl mx-auto">
        {activeView === 'dashboard' && (
          <div className="space-y-12 animate-in fade-in slide-in-from-bottom-6 duration-700">
            <section className="space-y-4">
              <div className="flex items-center gap-4"><h2 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Information & Resources</h2><div className="flex-1 h-px bg-slate-200/50" /></div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <NavCard title="Program Information" description="Details around fee, intakes and available programs" icon="📖" onClick={() => setActiveView('catalogue')} />
              </div>
            </section>
            <section className="space-y-4">
              <div className="flex items-center gap-4"><h2 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Application Management</h2><div className="flex-1 h-px bg-slate-200/50" /></div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {(isAdmin || isAgent || isAgentMaster) && (
                  <NavCard 
                    title="Submit Application"  
                    description="Create and submit applications." 
                    icon="📤" 
                    onClick={() => setActiveView('applications')} />
                )}
                {(isAdmin || isAppsAndVisa || isB2B) && (
                  <NavCard title="Evaluate Applications" description="Assess submissions and manage program outcomes." icon="🧐" onClick={() => setActiveView('evaluation')} />
                )}
              </div>
            </section>
            {(isAdmin || isAppsAndVisa || isB2B) && (
              <section className="space-y-4">
                <div className="flex items-center gap-4"><h2 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Track Progression</h2><div className="flex-1 h-px bg-slate-200/50" /></div>
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  <NavCard title="Progression Pipeline" description="Track overseas progression for enrolled students." icon="🎯" onClick={() => setActiveView('board')} />
                </div>
              </section>
            )}
            {isAdmin && (
              <section className="space-y-4">
                <div className="flex items-center gap-4"><h2 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">System Setup</h2><div className="flex-1 h-px bg-slate-200/50" /></div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <NavCard title="Global Setup" description="Configure master data and university mappings." icon="⚙️" onClick={() => setActiveView('settings')} />
                </div>
              </section>
            )}
          </div>
        )}

        {activeView === 'applications' && (isAdmin || isAgent || isAgentMaster) && (
          <SubmitApplication 
            user={user}
            users={users} 
            activeTab={appSectionTab} 
            onTabChange={setAppSectionTab} 
            onBack={() => setActiveView('dashboard')}
            settings={settings} 
            applicants={applicants} 
            applications={applications} 
            onSaveApplicant={handleSaveApplicant}
            onDeleteApplicant={handleDeleteApplicant} 
            applicationRecords={applications} 
            onSaveApplication={handleSaveApplication}
          />
        )}

        {activeView === 'evaluation' && (isAdmin || isAppsAndVisa || isB2B) && (
          <EvaluateApplication 
            users={users} settings={settings} applications={applications}
            onUpdateApplication={actions.updateApplication}
            onSaveStudent={actions.saveStudent} 
            onBack={() => setActiveView('dashboard')} 
            currentUserId={user.uid}
            viewOnly={isB2B && !isAdmin}
          />
        )}

        {activeView === 'catalogue' && <ProgramInformation onClose={() => setActiveView('dashboard')} />}

        {activeView === 'board' && (isAdmin || isAppsAndVisa || isB2B) && (
          <div className="animate-in fade-in duration-500">
            <div className="flex justify-between items-end mb-8">
              <div>
                <h2 className="text-3xl font-black text-slate-900 tracking-tighter uppercase leading-none">Progression Pipeline</h2>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Live Student Lifecycle Tracking</p>
              </div>
              {!isB2B && (
                <button onClick={() => setIsFormOpen(true)} className="bg-[#0464AD] text-white px-8 py-3 rounded-2xl text-[10px] font-black uppercase shadow-xl hover:bg-[#0091D0] transition-all">New Enrollment</button>
              )
              }
            </div>

            {/* PIPELINE FILTERS BAR */}
            <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm mb-8 grid grid-cols-1 md:grid-cols-4 lg:grid-cols-7 gap-4">
              {/* Target Country Filter */}
              <div className="space-y-1.5">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Destination Country</label>
                <select 
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-[10px] font-bold outline-none focus:ring-2 focus:ring-[#0464AD]"
                  value={boardFilters.targetCountry}
                  onChange={(e) => setBoardFilters({...boardFilters, targetCountry: e.target.value})}
                >
                  <option value="All Countries">All Countries</option>
                  {(settings?.countries || []).map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              {/* University Filter */}
              <div className="space-y-1.5">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">University</label>
                <select 
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-[10px] font-bold outline-none focus:ring-2 focus:ring-[#0464AD]"
                  value={boardFilters.university}
                  onChange={(e) => setBoardFilters({...boardFilters, university: e.target.value})}
                >
                  <option value="All Universities">All Universities</option>
                  {(settings?.universities || []).map(u => <option key={u.id} value={u.name}>{u.name}</option>)}
                </select>
              </div>

              {/* Commencing Term Filter */}
              <div className="space-y-1.5">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Commencing Term</label>
                <div className="flex gap-1">
                  <select 
                    className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-2 py-2 text-[9px] font-bold outline-none"
                    value={boardFilters.termMonth}
                    onChange={(e) => setBoardFilters({...boardFilters, termMonth: e.target.value})}
                  >
                    <option value="All Months">Month</option>
                    {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <select 
                    className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-2 py-2 text-[9px] font-bold outline-none"
                    value={boardFilters.termYear}
                    onChange={(e) => setBoardFilters({...boardFilters, termYear: e.target.value})}
                  >
                    <option value="All Years">Year</option>
                    {[2024, 2025, 2026, 2027].map(y => <option key={y} value={String(y)}>{y}</option>)}
                  </select>
                </div>
              </div>

              {/* Expected Progression Filter */}
              <div className="space-y-1.5">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Expected Progression</label>
                <div className="flex gap-1">
                  <select 
                    className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-2 py-2 text-[9px] font-bold outline-none"
                    value={boardFilters.progressionMonth}
                    onChange={(e) => setBoardFilters({...boardFilters, progressionMonth: e.target.value})}
                  >
                    <option value="All Months">Month</option>
                    {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <select 
                    className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-2 py-2 text-[9px] font-bold outline-none"
                    value={boardFilters.progressionYear}
                    onChange={(e) => setBoardFilters({...boardFilters, progressionYear: e.target.value})}
                  >
                    <option value="All Years">Year</option>
                    {[2024, 2025, 2026, 2027].map(y => <option key={y} value={String(y)}>{y}</option>)}
                  </select>
                </div>
              </div>

              {/* Source Agent Filter */}
              <div className="space-y-1.5">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Source Agent</label>
                <select 
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-[10px] font-bold outline-none focus:ring-2 focus:ring-[#0464AD]"
                  value={boardFilters.agent}
                  onChange={(e) => setBoardFilters({...boardFilters, agent: e.target.value})}
                >
                  <option value="All Agents">All Agents</option>
                  {(settings?.sourceAgents || []).map(a => {
                    const name = typeof a === 'string' ? a : a.name;
                    return <option key={name} value={name}>{name}</option>;
                  })}
                </select>
              </div>

              {/* TWg Program Filter */}
              <div className="space-y-1.5">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">TWG Program</label>
                <select 
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-[10px] font-bold outline-none focus:ring-2 focus:ring-[#0464AD]"
                  value={boardFilters.twgProgram}
                  onChange={(e) => setBoardFilters({...boardFilters, twgProgram: e.target.value})}
                >
                  <option value="All Programs">All Programs</option>
                  {(settings?.twgPrograms || []).map(p => (
                     <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>

              {/* Reset Button */}
              <div className="flex items-end pb-0.5">
                <button 
                  onClick={() => setBoardFilters({
                    university: 'All Universities', 
                    targetCountry: 'All Countries', 
                    progressionMonth: 'All Months', 
                    progressionYear: 'All Years', 
                    termMonth: 'All Months', 
                    termYear: 'All Years', 
                    agent: 'All Agents',
                    twgProgram: 'All Programs'
                   })}

                  className="text-[9px] font-black text-slate-400 hover:text-red-500 uppercase tracking-widest transition-colors mb-2 ml-2"
                >
                  Reset Filters
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 items-start">
              {STAGES.map(stage => (
                <div key={stage} className="flex flex-col gap-4">
                  <div className="flex items-center justify-between px-2">
                    <h3 className="text-[10px] font-black uppercase text-slate-400 tracking-widest">{stage}</h3>
                    <span className="text-[9px] font-black text-slate-300 bg-slate-50 px-1.5 py-0.5 rounded">
                      {filteredBoardStudents.filter(s => s.stage === stage).length}
                    </span>
                  </div>
                  <div className="space-y-3 min-h-[400px] p-2 bg-slate-100/50 rounded-[1.5rem] border border-dashed border-slate-200">
                    {filteredBoardStudents.filter(s => s.stage === stage).map(student => (
                      <StudentCard 
                        key={student.id} 
                        student={student} 
                        onClick={() => { setSelectedStudent(student); setActiveView('profile'); }} 
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeView === 'profile' && liveSelectedStudent && (isAdmin || isAppsAndVisa || isB2B) && (
          <StudentProfile 
            student={liveSelectedStudent} // Switched to live data
            currentUser={currentUserData} settings={settings} users={users}
            viewOnly={isB2B && !isAdmin}
            onClose={() => { setActiveView('board'); setSelectedStudent(null); }}
            onAddTimelineEvent={actions.addTimelineEvent}
            onUpdateStage={(id, stage, reason) => {
              actions.saveStudent({ stage }, id);
              actions.addTimelineEvent(id, {
                type: 'status',
                content: `Stage changed to ${stage}`,
                reason,
                timestamp: new Date().toISOString(),
                user: currentUserData?.name || 'Agent'
              });
            }}
            onUpdateStudent={actions.saveStudent}
            onUpdateDocuments={actions.updateStudentDocuments}
            onEditProfile={(student) => {
              setSelectedStudent(student);
              setIsFormOpen(true);
            }}
          />
        )}

        {activeView === 'settings' && isAdmin && (
          <SettingsScreen 
            settings={settings} 
            users={users}
            onUpdate={actions.updateSettings} 
            onClose={() => setActiveView('dashboard')} 
          />
        )}

        {activeView === 'users' && isAdmin && (
          <UserManagement 
            settings={settings} users={users} currentUserId={user.uid} 
            onUpdateRole={actions.updateUserRole} onUpdateStatus={actions.updateUserStatus} 
            onUpdateUser={actions.updateUser} onDeleteUser={actions.deleteUser} 
            onClose={() => setActiveView('dashboard')} 
          />
        )}
      </main>

      <GeminiAssistant 
        settings={settings} 
        botKnowledge={botKnowledge} 
        students={students} 
        applications={applications} 
        applicants={applicants}
        users={users}
        currentDate={currentDate}
        currentUser={currentUserData}
      />

      {isFormOpen && (
        <FormModal 
          student={liveSelectedStudent} // Switched to live data
          settings={settings}
          onClose={() => { 
            setIsFormOpen(false); 
            if (activeView !== 'profile') setSelectedStudent(null);
          }}
          onSave={async (data, id) => { 
            await actions.saveStudent(data, id); 
            setIsFormOpen(false); 
            // Re-sync selection if in profile view
            if (activeView === 'profile') {
               const updated = students.find(s => s.id === id);
               setSelectedStudent(updated);
            }
          }}
          onDelete={async (id) => {
            await actions.deleteStudent(id);
            setIsFormOpen(false);
            setSelectedStudent(null);
            setActiveView('board');
          }}
        />
      )}

      {/* NEW: USER PROFILE MODAL */}
      {showProfile && currentUserData && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4" onClick={() => setShowProfile(false)}>
          <div className="bg-white rounded-[2.5rem] w-full max-w-sm shadow-2xl p-10 animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
            <div className="flex flex-col items-center text-center">
              <div className="w-20 h-20 bg-[#0464AD] text-white rounded-3xl flex items-center justify-center text-3xl font-black mb-6 shadow-xl shadow-blue-100">
                {currentUserData.name?.charAt(0).toUpperCase()}
              </div>
              <h3 className="text-2xl font-black text-slate-900 tracking-tighter uppercase">{currentUserData.name}</h3>
              <span className="mt-2 px-3 py-1 bg-blue-50 text-[#0464AD] text-[10px] font-black uppercase tracking-widest rounded-lg border border-blue-100">
                {currentUserData.role}
              </span>
              
              <div className="w-full mt-10 space-y-6 text-left border-t border-slate-100 pt-8">
                <div>
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Email Address</p>
                  <p className="text-sm font-bold text-slate-700">{currentUserData.email}</p>
                </div>
                {currentUserData.agentAssociation && (
                  <div>
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Linked Organization</p>
                    <p className="text-sm font-bold text-slate-700 uppercase">{currentUserData.agentAssociation}</p>
                  </div>
                )}
                <div>
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Account Status</p>
                  <p className="text-sm font-bold text-emerald-500 uppercase">{currentUserData.status}</p>
                </div>
              </div>

              <button 
                onClick={() => setShowProfile(false)}
                className="w-full mt-10 bg-slate-900 text-white py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all shadow-lg"
              >
                Close Profile
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;